/* eslint-disable fp/no-mutation */
import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import {
  updateANCCAccreditation,
  fetchEventCompliances,
  fetchANCCAccreditation,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { ANCCAccreditation } from 'src/v3/core/types'

type ANCCFieldEditableProps = {
  eventID: string
  accreditation: ANCCAccreditation
  fieldName: keyof Pick<
    ANCCAccreditation,
    | 'courseCode'
    | 'expirationDate'
    | 'targetAudience'
    | 'educationalNeeds'
    | 'competency'
    | 'professionalSource'
  >
  fieldLabel: string
  fieldType: 'text' | 'date' | 'select' | 'textarea' | 'multiselect'
  options?: string[] // For select fields
  required?: boolean
  viewMode?: 'inline' | 'modal' // inline for planning, modal for compliance
  onClose?: () => void // Called when modal is closed (modal mode only)
}

let targetAudiences = [
  'Registered Nurse',
  'Paramedic',
  'EMT',
  'Respiratory Therapist',
  'Advanced Practice Nurse',
  'Other',
]

let educationalNeeds = ['Knowledge', 'Skill', 'Practice']

export default ({
  eventID,
  accreditation,
  fieldName,
  fieldLabel,
  fieldType,
  options = [],
  required = false,
  viewMode = 'inline',
  onClose,
}: ANCCFieldEditableProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [isEditing, setIsEditing] = useState(false)
  let [loading, setLoading] = useState(false)

  let currentValue = accreditation?.[fieldName] || ''

  // Format dates for display
  if (fieldType === 'date' && currentValue) {
    let date = new Date(currentValue as string)
    if (!Number.isNaN(date.getTime())) {
      ;[currentValue] = [date.toISOString().split('T')[0]]
    }
  }

  let form = useForm({
    defaultValues: {
      [fieldName]: currentValue,
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        let updateValue = value[fieldName]

        // Convert date strings to ISO format for API
        if (fieldType === 'date' && updateValue) {
          updateValue = new Date(updateValue as string).toISOString()
        }

        let fieldUpdates: any = {
          id: accreditation.id,
          [fieldName]: updateValue,
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(updateANCCAccreditation(fieldUpdates))
        // Refetch both accreditation and compliance statuses
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchANCCAccreditation(eventID))
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchEventCompliances())
        setIsEditing(false)

        // Close modal if in modal mode
        if (viewMode === 'modal' && onClose) {
          onClose()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update ANCC accreditation field:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  let renderFieldDisabled = () => {
    if (fieldType === 'date') {
      return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            sx={{ bgcolor: 'background.paperDark' }}
            value={
              currentValue && typeof currentValue === 'string'
                ? dayjs(currentValue)
                : null
            }
            disabled
            slotProps={{
              textField: {
                fullWidth: true,
                slotProps: { htmlInput: { placeholder: 'Not set' } },
              },
              openPickerButton: {
                sx: { display: 'none' }, // Hide calendar button in disabled/display mode
              },
            }}
          />
        </LocalizationProvider>
      )
    }

    if (fieldType === 'select' || fieldType === 'multiselect') {
      let displayValue =
        fieldType === 'multiselect' && Array.isArray(currentValue)
          ? currentValue.join(', ')
          : String(currentValue || '')

      return (
        <FormControl fullWidth>
          <Select
            sx={{ bgcolor: 'background.paperDark' }}
            value=""
            displayEmpty
            disabled
          >
            <MenuItem value="">{displayValue || <em>Not set</em>}</MenuItem>
          </Select>
        </FormControl>
      )
    }

    if (fieldType === 'textarea') {
      return (
        <TextField
          sx={{ bgcolor: 'background.paperDark' }}
          fullWidth
          multiline
          minRows={3}
          value={currentValue || ''}
          placeholder="Not set"
          disabled
          aria-label={fieldLabel}
        />
      )
    }

    // Default text field
    return (
      <TextField
        sx={{ bgcolor: 'background.paperDark' }}
        fullWidth
        value={currentValue || ''}
        placeholder="Not set"
        disabled
        aria-label={fieldLabel}
      />
    )
  }

  let renderField = () => {
    if (fieldType === 'date') {
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                sx={{ bgcolor: 'background.paperDark' }}
                value={
                  field.state.value && typeof field.state.value === 'string'
                    ? dayjs(field.state.value)
                    : null
                }
                onChange={(val) => {
                  if (!val) {
                    field.handleChange('')
                  } else {
                    field.handleChange(val.format())
                  }
                }}
                onClose={field.handleBlur}
                disabled={loading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    onBlur: field.handleBlur,
                  },
                }}
              />
            </LocalizationProvider>
          )}
        />
      )
    }

    if (fieldType === 'select' || fieldType === 'multiselect') {
      let selectOptions =
        fieldName === 'targetAudience'
          ? targetAudiences
          : fieldName === 'educationalNeeds'
            ? educationalNeeds
            : options
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <FormControl fullWidth>
              <Select
                sx={{ bgcolor: 'background.paperDark' }}
                value={
                  field.state.value || (fieldType === 'multiselect' ? [] : '')
                }
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={loading}
                multiple={fieldType === 'multiselect'}
              >
                {selectOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )
    }

    if (fieldType === 'textarea') {
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              multiline
              minRows={3}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              disabled={loading}
              aria-label={fieldLabel}
            />
          )}
        />
      )
    }

    // Default text field
    return (
      <form.Field
        name={fieldName}
        children={(field) => (
          <TextField
            sx={{ bgcolor: 'background.paperDark' }}
            fullWidth
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            disabled={loading}
            aria-label={fieldLabel}
          />
        )}
      />
    )
  }

  let renderContent = () => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          minHeight: '24px',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
          }}
        >
          {fieldLabel}
          {required && (
            <Typography color="error" component="span" aria-label="required">
              {' '}
              *
            </Typography>
          )}
        </Typography>
      </Box>

      {!isEditing ? (
        <Box sx={{ position: 'relative' }}>
          {renderFieldDisabled()}
          <Button
            variant="text"
            size="small"
            onClick={() => {
              form.setFieldValue(fieldName, currentValue)
              setIsEditing(true)
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              minWidth: 'auto',
              px: 1,
            }}
            aria-label={`Edit ${fieldLabel}`}
          >
            Edit
          </Button>
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>
            {(fieldType === 'select' || fieldType === 'multiselect') && (
              <InputLabel sx={{ mb: 1, fontSize: '0.875rem' }}>
                {fieldLabel}
              </InputLabel>
            )}
            {renderField()}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => form.handleSubmit()}
              disabled={loading}
              aria-label={`Save changes to ${fieldLabel}`}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsEditing(false)
                form.reset()
                // Close modal if in modal mode
                if (viewMode === 'modal' && onClose) {
                  onClose()
                }
              }}
              disabled={loading}
              aria-label={`Cancel editing ${fieldLabel}`}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )

  // If modal mode, render the content directly (parent will wrap in modal)
  if (viewMode === 'modal') {
    return renderContent()
  }

  // Inline mode - render directly
  return renderContent()
}
